import React, { useContext, useEffect, useState } from 'react';
import { Input, message } from 'antd';
import AddOrganizationDialog from './AddOrganizationDialog';
import {
  Container,
  MainContent,
  ContentWrapper,
  HeaderSection,
  HeaderContent,
  HeaderTextContainer,
  PageTitle,
  PageSubtitle,
  PrimaryButton,
  CardContainer,
  SearchFilterContainer,
  SearchInputWrapper,
  ResultsCount,
  ResultsCountNumber,
  GridContainer,
  OrganizationCard,
  CardHeader,
  CardTitle,
  CardMeta,
  CardMetaText,
  CardMetaDivider,
  StatusBadge,
  UsageStatsContainer,
  UsageStatItem,
  UsageStatLeft,
  UsageStatLabel,
  UsageStatValue,
  CardActions,
  ActionButton,
  SecondaryButton,
  DangerButton,
  LoaderContainer,
  EmptyStateContainer,
  EmptyStateIcon,
  EmptyStateTitle,
  EmptyStateMessage,
  EmptyStateSearchQuery,
  PermissionsLink
} from './styles';
import { withTranslation } from 'react-i18next';
import { ORGANIZATION_PAGE } from './translationValues';
import { createNewOrganization, updateOrgStatus, getAllOrganizations, signInOrganization, updateOrgFeatures } from '../../utils/services/manageOrganizationAPI';
import { AppStoreContext } from '../../context/appStoreContext';
import Loader from '../../components/Loader';
import ConfirmationModal from '../../components/ConfirmationModal/ConfirmationModal';
import { PlusIcon, SearchIcon, FileTextIcon, MailIcon, MessageSquareIcon, ExternalLinkIcon, BanIcon, Trash2Icon, SearchXIcon, OrganizationIcon } from '../../images/svgIcons';
import FeaturesModal from './FeaturesModal';

const NAMESPACES=[];

const ManageOrganizations = ({ t }) => {
  const { isOrganizationSignedIn } = useContext(AppStoreContext);
  const [organizations, setOrganizations] = useState([]);
  const [confirmDeleteOrgToggle, setConfirmDeleteOrgToggle] = useState(false);
  const [confirmInactivateOrgToggle, setConfirmInactivateOrgToggle] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isCreatingOrg, setIsCreatingOrg] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedOrgId, setSelectedOrgId] = useState(null);
  const [isFeaturesModalOpen, setIsFeaturesModalOpen] = useState(false);
  const [selectedOrganization, setSelectedOrganization] = useState(null);

  const fetchOrganizations = async() => {
    setLoading(true);
    try {
        const response = await getAllOrganizations();
        if(response.data?.length > 0) {
            setOrganizations(response.data);
        } else setOrganizations([]);
    } catch (error) {
        message.error('Failed to fetch organizations list')
        console.error(error);
    } finally {
        setLoading(false);
    }
  }

  const handleSaveFeatures = async (data) => {
    if(selectedOrganization.status === 'inactive') {
      message.error('Cannot modify features for inactive organization');
      return;
    }
    try {
      const response = await updateOrgFeatures(data);
      if(response.statusCode === 6000) {
        setOrganizations(organizations.map(org =>
          org.id === data.orgId ? { ...org, features: data.data } : org
        ));
        message.success('Features updated successfully')
      }
    } catch (error) {
      message.error('Failed to update features');
      console.error(error)
    }
  };

  const handleOpenFeaturesModal = (org) => {
    setSelectedOrganization(org);
    setIsFeaturesModalOpen(true);
  };

  const addOrganization = async (newOrg) => {
    try {
        const response = await createNewOrganization({...newOrg, name: newOrg.organizationName});
        if(response.statusCode === 1900) {
            message.success(t(ORGANIZATION_PAGE.ORGANIZATION_CREATED.key, ORGANIZATION_PAGE.ORGANIZATION_CREATED.value))
            const organization = {
                ...newOrg,
                id: response.data,
                status: 'active',
                usage: {
                    documents: 0,
                    emails: 0,
                    messages: 0,
                },
            };
            setOrganizations([organization, ...organizations]);
            setIsAddDialogOpen(false);
        } else if(response.statusCode === 1901) {
            message.error('User already exists');
            setIsCreatingOrg(false);
        }
    } catch (error) {
        message.error(t(ORGANIZATION_PAGE.CREATE_ORG_FAILED.key, ORGANIZATION_PAGE.CREATE_ORG_FAILED.value));
        console.error(error);
        setIsAddDialogOpen(false);
        return;
    }
  };

  const inactivateOrganization = async (organizationId, type) => {
    try {
      const response = await updateOrgStatus(organizationId, 'inactive');
      if(response.statusCode === 1926) {
        message.success(`${type}d organization successfully`);
        const updatedOrgs = organizations.map(org => org.id === organizationId ? {...org, status: 'inactive'}: org);
        setOrganizations(updatedOrgs);
        setConfirmDeleteOrgToggle(false);
        setConfirmInactivateOrgToggle(false);
      } else message.error(`Failed to ${type.toLowerCase()} the organization.`);
    } catch (err) {
      message.error(`Failed to ${type.toLowerCase()} the organization.`);
      console.error(err);
    }
  }

  const activateOrganization = async (organizationId) => {
    try {
      const response = await updateOrgStatus(organizationId, 'active');
      if(response.statusCode === 1926) {
        message.success(`Activated organization successfully`);
        const updatedOrgs = organizations.map(org => org.id === organizationId ? {...org, status: 'active'}: org);
        setOrganizations(updatedOrgs);
      } else message.error(`Failed to activate the organization.`);
    } catch (err) {
      message.error(`Failed to activate the organization.`);
      console.error(err);
    }
  }

  const selectOrganization = async (orgId) => {
    try {
      const response = await signInOrganization(orgId);
      if(response.statusCode === 1928) {
        localStorage.setItem('isOrganizationSignedIn', 'true');
        window.open(`/`, '_self');
      }
    } catch(err) {
        message.error('Failed to sign in.')
        console.error(err);
    }
  }

  useEffect(() => {
    fetchOrganizations();
  }, [])
  
  const filteredCount = organizations?.filter(org =>
    org?.organizationName?.toLowerCase().includes(searchQuery.toLowerCase())
  ).length;

  const filteredOrganizations = organizations
    .filter(org =>
      org?.organizationName?.toLowerCase().includes(searchQuery.toLowerCase())
    );

  return (
    <Container style={{ display: 'flex', height: '100vh', background: '#fcf7f5' }}>

      <MainContent>
        <ContentWrapper>
          <HeaderSection>
            <HeaderContent>
              <HeaderTextContainer>
                <PageTitle>{t(ORGANIZATION_PAGE.PAGE_TITLE.key, ORGANIZATION_PAGE.PAGE_TITLE.value)}</PageTitle>
                <PageSubtitle>
                  {t(ORGANIZATION_PAGE.PAGE_DESCRIPTION.key, ORGANIZATION_PAGE.PAGE_DESCRIPTION.value)}
                </PageSubtitle>
              </HeaderTextContainer>
              {!loading && organizations.length > 0 && <PrimaryButton onClick={() => setIsAddDialogOpen(true)}>
                <PlusIcon />
                {t(ORGANIZATION_PAGE.ADD_ORG_BTN_LABEL.key, ORGANIZATION_PAGE.ADD_ORG_BTN_LABEL.value)}
              </PrimaryButton>}
            </HeaderContent>
          </HeaderSection>

          <div style={{ marginBottom: '24px' }}>
            <CardContainer>
              <SearchFilterContainer>
                <SearchInputWrapper>
                  <div style={{ position: 'relative' }}>
                    <div style={{ position: 'absolute', left: '16px', top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none', color: '#9ca3af' }}>
                      <SearchIcon />
                    </div>
                    <Input
                      placeholder="Search by Organization Name..."
                      value={searchQuery}
                      disabled={loading || organizations.length === 0}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      style={{
                        paddingLeft: '48px',
                        height: '48px',
                        borderRadius: '12px',
                        backgroundColor: '#fcf7f5',
                        border: '1px solid #e5e7eb'
                      }}
                    />
                  </div>
                </SearchInputWrapper>

                <div style={{ display: 'flex', gap: '12px' }}></div>
              </SearchFilterContainer>
            </CardContainer>
          </div>

          {loading && <LoaderContainer>
            <Loader />
            </LoaderContainer>}

          {!loading && organizations.length === 0 && <EmptyStateContainer>
              <EmptyStateIcon>
                <OrganizationIcon />
              </EmptyStateIcon>
              <EmptyStateTitle>{t(ORGANIZATION_PAGE.NO_ORGS_TITLE.key, ORGANIZATION_PAGE.NO_ORGS_TITLE.value)}</EmptyStateTitle>
              <EmptyStateMessage>
                {t(ORGANIZATION_PAGE.NO_ORGS_DESCRIPTION.key, ORGANIZATION_PAGE.NO_ORGS_DESCRIPTION.value)}
              </EmptyStateMessage>
              <PrimaryButton onClick={() => setIsAddDialogOpen(true)} style={{ marginTop: '8px' }}>
                <PlusIcon />
                {t(ORGANIZATION_PAGE.ADD_FIRST_ORG.key, ORGANIZATION_PAGE.ADD_FIRST_ORG.value)}
              </PrimaryButton>
            </EmptyStateContainer>}

          {!loading && organizations.length > 0 && <ResultsCount>
            {t(ORGANIZATION_PAGE.SHOWING_LABEL.key, ORGANIZATION_PAGE.SHOWING_LABEL.value)} <ResultsCountNumber>{filteredCount}</ResultsCountNumber> {t(ORGANIZATION_PAGE.OF_LABEL.key, ORGANIZATION_PAGE.OF_LABEL.value)} <ResultsCountNumber>{organizations.length}</ResultsCountNumber> {t(ORGANIZATION_PAGE.ORGANIZATIONS_LABEL.key, ORGANIZATION_PAGE.ORGANIZATIONS_LABEL.value)}
          </ResultsCount>}

          {!loading && organizations.length > 0 && filteredOrganizations.length === 0 &&
            <EmptyStateContainer>
                <EmptyStateIcon>
                  <SearchXIcon />
                </EmptyStateIcon>
                <EmptyStateTitle>{t(ORGANIZATION_PAGE.NO_ORGS_MATCHES_TITLE.key, ORGANIZATION_PAGE.NO_ORGS_MATCHES_TITLE.value)}</EmptyStateTitle>
                <EmptyStateMessage>
                  {t(ORGANIZATION_PAGE.NO_ORGS_MATCHING.key, ORGANIZATION_PAGE.NO_ORGS_MATCHING.value)} <EmptyStateSearchQuery>"{searchQuery}"</EmptyStateSearchQuery>. {t(ORGANIZATION_PAGE.TRY_CLEARING_SEARCH.key, ORGANIZATION_PAGE.TRY_CLEARING_SEARCH.value)}
                </EmptyStateMessage>
                <PrimaryButton onClick={() => setSearchQuery('')} style={{ marginTop: '8px' }}>
                  {t(ORGANIZATION_PAGE.CLEAR_SEARCH.key, ORGANIZATION_PAGE.CLEAR_SEARCH.value)}
                </PrimaryButton>
            </EmptyStateContainer>}

          {!loading && organizations.length > 0 && <GridContainer>
            {filteredOrganizations.map(org => (
              <OrganizationCard key={org.id}>
                <CardHeader>
                  <div style={{ flex: 1 }}>
                    <CardTitle>{org.organizationName?.toUpperCase() || ''}</CardTitle>
                    <CardMeta>
                      <CardMetaText>{org.type}</CardMetaText>
                      <CardMetaDivider>•</CardMetaDivider>
                      <StatusBadge $isActive={org.status === 'active'}>
                        {org.status}
                      </StatusBadge>
                      <PermissionsLink onClick={() => handleOpenFeaturesModal(org)}>
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
                          </svg>
                          Features
                        </PermissionsLink>
                    </CardMeta>
                  </div>
                </CardHeader>

                <UsageStatsContainer>
                  <UsageStatItem $bgColor="#eff6ff">
                    <UsageStatLeft>
                      <FileTextIcon color="#2563eb" />
                      <UsageStatLabel>Documents</UsageStatLabel>
                    </UsageStatLeft>
                    <UsageStatValue>{org.documentCount || 0}</UsageStatValue>
                  </UsageStatItem>
                  
                  <UsageStatItem $bgColor="#faf5ff">
                    <UsageStatLeft>
                      <MailIcon color="#9333ea" />
                      <UsageStatLabel>Emails</UsageStatLabel>
                    </UsageStatLeft>
                    <UsageStatValue>{org.emailCount || 0}</UsageStatValue>
                  </UsageStatItem>
                  
                  <UsageStatItem $bgColor="#f0fdf4">
                    <UsageStatLeft>
                      <MessageSquareIcon color="#16a34a" />
                      <UsageStatLabel>Messages</UsageStatLabel>
                    </UsageStatLeft>
                    <UsageStatValue>{org.smsCount || 0}</UsageStatValue>
                  </UsageStatItem>
                </UsageStatsContainer>

                <CardActions>
                  {!isOrganizationSignedIn &&
                  <ActionButton disabled={org.status === 'inactive'} onClick={() => selectOrganization(org.id)}>
                    <ExternalLinkIcon />
                    {t(ORGANIZATION_PAGE.OPEN_BTN_LABEL.key, ORGANIZATION_PAGE.OPEN_BTN_LABEL.value)}
                  </ActionButton>}
                  {org.status === 'active' ? <SecondaryButton onClick={() => {
                    setSelectedOrgId(org.id);
                    setConfirmInactivateOrgToggle(true);
                  }}>
                    <BanIcon color='#A31616' />
                    {t(ORGANIZATION_PAGE.INACTIVATE_BTN_LABEL.key, ORGANIZATION_PAGE.INACTIVATE_BTN_LABEL.value)}
                  </SecondaryButton>
                  : <SecondaryButton style={{ backgroundColor: '#dfdfdf', color: 'black' }} onClick={() => {
                    setSelectedOrgId(org.id);
                    activateOrganization(org.id);
                  }}>
                    <BanIcon color='#16A34A' />
                    {t(ORGANIZATION_PAGE.ACTIVATE_BTN_LABEL.key, ORGANIZATION_PAGE.ACTIVATE_BTN_LABEL.value)}
                  </SecondaryButton>}
                  <DangerButton disabled={org.status === 'inactive'} onClick={() => {
                    setSelectedOrgId(org.id);
                    setConfirmDeleteOrgToggle(true);
                  }}>
                    <Trash2Icon />
                  </DangerButton>
                </CardActions>
              </OrganizationCard>
            ))}
            <ConfirmationModal
                showImage={false}
                visibility={confirmInactivateOrgToggle}
                onCancel={() => {setConfirmInactivateOrgToggle(false); setSelectedOrgId(null);}}
                onConfirm={() => inactivateOrganization(selectedOrgId, 'Inactivate')}
                confirmButtonText={t(
                    ORGANIZATION_PAGE.CONFIRM_BUTTON.key,
                    ORGANIZATION_PAGE.CONFIRM_BUTTON.value,
                )}
                confirmationText={t(
                    ORGANIZATION_PAGE.CONFIRM_MODAL_INACTIVATE_DESCRIPTION.key,
                    ORGANIZATION_PAGE.CONFIRM_MODAL_INACTIVATE_DESCRIPTION.value,
                )}
            />
            <ConfirmationModal
                showImage={false}
                visibility={confirmDeleteOrgToggle}
                onCancel={() => {setConfirmDeleteOrgToggle(false); setSelectedOrgId(null);}}
                onConfirm={() => inactivateOrganization(selectedOrgId, 'Delete')}
                confirmButtonText={t(
                    ORGANIZATION_PAGE.CONFIRM_BUTTON.key,
                    ORGANIZATION_PAGE.CONFIRM_BUTTON.value,
                )}
                confirmationText={t(
                    ORGANIZATION_PAGE.CONFIRM_MODAL_DELETE_DESCRIPTION.key,
                    ORGANIZATION_PAGE.CONFIRM_MODAL_DELETE_DESCRIPTION.value,
                )}
            />
          </GridContainer>}
        </ContentWrapper>
      </MainContent>

      <AddOrganizationDialog
        open={isAddDialogOpen}
        onOpenChange={setIsAddDialogOpen}
        onAdd={addOrganization}
        isCreatingOrg={isCreatingOrg}
        setIsCreatingOrg={setIsCreatingOrg}
      />

      <FeaturesModal
        open={isFeaturesModalOpen}
        onOpenChange={setIsFeaturesModalOpen}
        organization={selectedOrganization}
        onSave={handleSaveFeatures}
      />
    </Container>
  );
}
export default withTranslation(NAMESPACES)(ManageOrganizations);
